/**
 * ==========================================================================
 *  THE PUNCH
 * ==========================================================================
 *  Kimmo's right hand, thrown once per run at most, at the one thing in the
 *  level that has to be hit rather than opened: the pane in Einon pirtti's
 *  south window. `Ikkuna` decides on a simulation step that the glass went;
 *  this is only what that looked like, and nothing here is ever read back.
 *
 *  Same three tricks as the bottle and the klapit it shares a camera with:
 *  parented to the camera, drawn with depth testing off and last, and left out
 *  of the fog. Boxes rather than a photograph for the same reason the klapit
 *  are boxes - the venue is built out of them, and a photographed hand in a
 *  world of blocks would be the bottle's joke told twice.
 *
 *  ------------------------------------------------------------------------
 *   ONE SHOT, NOT A STATE
 *  ------------------------------------------------------------------------
 *  The klapit are a *state* - there is wood in the hand or there is not, and
 *  the viewmodel eases between the two. This is an event: `punch()` starts a
 *  clock and the hand is gone when it runs out. There is no "is he punching"
 *  for anything else to ask, which is what makes it impossible for this to
 *  disagree with the simulation - it cannot be out of sync with a fact it does
 *  not hold.
 *
 *  Where the hand *is* lives in `Punch`, which has no three.js in it and is
 *  tested without a canvas. This file is the rig and the four boxes.
 * ==========================================================================
 */

import * as THREE from 'three';
import { FIST_VIEWMODEL as F } from '../config/viewmodel';
import { Punch } from './Punch';

export interface FistFrame {
  /** Render seconds since the last frame. */
  dt: number;
}

export class FistViewmodel {
  private readonly throw_ = new Punch();
  private readonly pivot = new THREE.Group();
  private readonly geometry = new THREE.BoxGeometry(1, 1, 1);
  private readonly materials: THREE.MeshBasicMaterial[] = [];
  private readonly knuckleMaterial: THREE.MeshBasicMaterial;

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    const material = (color: number): THREE.MeshBasicMaterial => {
      const made = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        fog: false,
      });
      this.materials.push(made);
      return made;
    };

    const skin = material(F.skin);
    this.knuckleMaterial = material(F.highlight);
    const sleeve = material(F.sleeveColor);

    // Built along -z, which is the way the camera looks: the knuckles are the
    // far end and the wrist the near one, so driving the whole group forward
    // drives the knuckles at the glass. It is deliberately a short rig - see
    // `FIST_VIEWMODEL.depth` for what a full forearm does to the lens.
    const part = (
      box: { width: number; height: number; depth: number },
      z: number,
      surface: THREE.Material,
      y = 0,
      x = 0,
    ): void => {
      const mesh = new THREE.Mesh(this.geometry, surface);
      mesh.renderOrder = 999;
      mesh.frustumCulled = false;
      mesh.scale.set(box.width, box.height, box.depth);
      mesh.position.set(x, y, z);
      this.pivot.add(mesh);
    };

    const nose = -F.fist.depth / 2;
    part(F.fist, nose, skin);
    // The knuckles stand a couple of millimetres proud of the front face
    // rather than flush with it: two coplanar faces flicker, and the one part
    // of this the eye actually lands on is the part that hits the glass.
    part(
      F.knuckles,
      nose - F.fist.depth / 2 - F.knuckles.depth / 2,
      this.knuckleMaterial,
      F.fist.height * 0.22,
    );
    // The thumb, wrapped over the front of the fingers. On a right fist that
    // is the left-hand side as you look down your own arm, which is also the
    // side the window is on - so it is the half of the hand you can see.
    part(F.thumb, nose - 0.01, skin, -F.fist.height * 0.1, -(F.fist.width / 2 - F.thumb.width / 2 + 0.012));
    // The wrist, and the whole of the arm there is. See `FIST_VIEWMODEL.wrist`.
    part(F.wrist, F.wrist.depth / 2, sleeve);

    this.pivot.visible = false;
    camera.add(this.pivot);
  }

  /** Throw it. Called on the render side, off the step the pane went. */
  punch(): void {
    this.throw_.start();
  }

  reset(): void {
    this.throw_.reset();
    this.pivot.visible = false;
    this.knuckleMaterial.color.setHex(F.highlight);
  }

  update(frame: FistFrame): void {
    const pose = this.throw_.update(frame.dt);
    if (!pose) {
      this.pivot.visible = false;
      this.knuckleMaterial.color.setHex(F.highlight);
      return;
    }

    // Taken from the camera every frame rather than cached: the FOV is a
    // slider the player can drag mid-run, and the aspect changes with the
    // window. Same reason `BeerViewmodel` does it.
    const halfHeight = Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2) * F.depth;
    // The bottle hugs the corner, so it is placed against the real half-width
    // and belongs further out the wider the window gets. A punch is thrown at
    // what you are *looking at*, so it must not: on a 2.7:1 window the same
    // fractions would land the hand out by the jamb instead of on the glass.
    // Capped at 16:9, which is the shape the poses were chosen against.
    const halfWidth = halfHeight * Math.min(this.camera.aspect, 16 / 9);

    this.pivot.visible = true;
    this.pivot.position.set(pose.x * halfWidth, pose.y * halfHeight, -F.depth);
    this.pivot.rotation.set(pose.pitch, pose.yaw, pose.roll);
    this.pivot.scale.setScalar(pose.scale);
    // Split knuckles from the moment they arrive, and they stay split for the
    // whole way back - which is the three seconds `GLASS_PAIN` is charging
    // for, said by the one part of the screen that can say why.
    this.knuckleMaterial.color.setHex(pose.landed ? F.cut : F.highlight);
    for (const material of this.materials) material.opacity = pose.opacity;
  }

  dispose(): void {
    this.camera.remove(this.pivot);
    this.geometry.dispose();
    for (const material of this.materials) material.dispose();
  }
}
